/**
 * Create-game screen (#150; M12, FR-8.5/9.5/9.6; #91 FR-11.1) — the host's first
 * step. Modernized to present the ONE remaining choice clearly and default the rest:
 *
 *   - **Board source is defaulted to dynamic generation.** The old "how do you want to
 *     build the board?" chooser is gone from the UI: a pack-less game is generated on
 *     the fly from the categories players suggest. The uploaded/BYO-pack path is kept
 *     in code but off the default screen — reachable only via `/host?source=pack`
 *     (preserves the deterministic deployed-e2e path and the future #69 re-entry).
 *   - **"Who runs the game?"** is presented as two self-describing cards
 *     ({@link HostModeCards}) so the host understands human-host vs auto-host (voice).
 *
 * The chosen `hostMode` is threaded into the create call (`createGame(..., hostMode)`)
 * by the rendered lobby; everything downstream reacts to `state.hostMode`, so the two
 * create paths stay unchanged.
 *
 * **Credits live here now (#171 review).** A paid game costs one credit at generation
 * (or at auto-host start), so the balance + get-more-credits slot is surfaced at THIS
 * step — before the host invites players — rather than in the lobby. A host who has run
 * out sees it (and can top up) up front instead of being blocked after gathering players.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { HostMode, HouseRules, SavedRulePreset } from '@jeopardy/shared';
import type { CreditBalance as CreditBalanceValue } from '../net/credits-api';
import { PackLobby } from './PackLobby';
import { DynamicLobby } from './DynamicLobby';
import { HostModeCards } from './HostModeCards';
import { HouseRulesChooser } from './HouseRulesChooser';
import { CreditBalance } from './CreditBalance';
import { loadRateCluesEnabled, saveRateCluesEnabled } from '../net/clue-rating-prefs';
import {
  deletePreset,
  loadHouseRulesPrefs,
  savePreset,
  stampLastUsed,
} from '../net/house-rules-api';

interface CreateGameProps {
  onGameReady: (ticket: string, joinCode: string) => void;
  /**
   * The host's signed guest token, used to read + top up their credit balance at the
   * create step (#171 review). Absent (dev with no identity) → the balance is hidden.
   */
  guestToken?: string | null;
  /**
   * Whether the host is a registered account (#125, D-125.5). Registered → the buy-credits
   * pack picker renders; a guest sees a sign-in steer. Defaults to `false`.
   */
  registered?: boolean;
  /**
   * On a checkout-success return (#197, FR-12.6.1), the credits just granted — passed to the
   * balance so it ties itself to the purchase with a "+N" chip. Undefined on a normal visit.
   */
  successDelta?: number;
}

export function CreateGame({
  onGameReady,
  guestToken = null,
  registered = false,
  successDelta,
}: CreateGameProps): React.JSX.Element {
  const [hostMode, setHostMode] = useState<HostMode>('HUMAN');
  // House Rules (#232): the resolved, bounded per-game options from the preset chooser +
  // Advanced drawer. `undefined` = Classic (default) → the create sends no houseRules and
  // behavior is byte-for-byte unchanged.
  const [houseRules, setHouseRules] = useState<HouseRules | undefined>(undefined);
  const [balance, setBalance] = useState<CreditBalanceValue | null>(null);
  // Host-local "Rate clues" enablement (#201, D201.12): persisted to localStorage, not to
  // game state / the wire. Only the host rates, so only the host's browser needs it.
  const [rateClues, setRateClues] = useState<boolean>(() => loadRateCluesEnabled());
  // Saved House Rules presets + last-used (#328, FR-19), server-side + account-scoped. Loaded
  // once on mount for a registered host; the chooser renders + applies them.
  const [savedPresets, setSavedPresets] = useState<SavedRulePreset[]>([]);
  const [lastUsed, setLastUsed] = useState<HouseRules | undefined>(undefined);
  const [searchParams] = useSearchParams();
  // Board source is dynamic by default; the uploaded-pack path is opt-in via the URL
  // (`/host?source=pack`) — kept for BYO-pack authors + deterministic e2e (#150).
  const usePack = searchParams.get('source') === 'pack';
  // A game costs a credit iff its start is a paid path: dynamic generation (always) or
  // auto-host voice (even on an uploaded pack). A human-hosted pack game is free, so it
  // never surfaces credits here. The dynamic default is paid regardless of host mode.
  const paidPath = !usePack || hostMode === 'AUTO';

  // Gate the create button on credits (#171 review, PR #179): a host who is out of credits
  // must not be able to create a paid game at all — the button is hidden here, not just
  // blocked at generate. Only block once the balance has actually loaded (`!== null`) so
  // the button never flashes-then-vanishes during the fetch; the server is the backstop.
  // A free (human-hosted pack) game is never gated.
  const outOfCredits =
    paidPath && guestToken !== null && balance !== null && balance.total <= 0;

  // Load the host's saved presets + last-used config once, for a registered host (#328).
  // Best-effort: a failure just leaves the built-ins. Guests never call it (server 403s).
  useEffect(() => {
    if (!registered || !guestToken) return;
    let cancelled = false;
    void loadHouseRulesPrefs().then((prefs) => {
      if (cancelled) return;
      setSavedPresets(prefs.presets);
      setLastUsed(prefs.lastUsed);
    });
    return () => {
      cancelled = true;
    };
  }, [registered, guestToken]);

  const handleSavePreset = useCallback(async (name: string, rules: HouseRules) => {
    const created = await savePreset(name, rules);
    setSavedPresets((prev) => [...prev, created]);
  }, []);

  const handleDeletePreset = useCallback(async (id: string) => {
    const remaining = await deletePreset(id);
    setSavedPresets(remaining);
  }, []);

  // Stamp the host's last-used rules on a successful create (#328, FR-19.3) — fire-and-forget,
  // off the create path — then hand off. Registered hosts only (the stamp route is gated).
  const handleGameReady = useCallback(
    (ticket: string, joinCode: string) => {
      if (registered && guestToken) void stampLastUsed(houseRules);
      onGameReady(ticket, joinCode);
    },
    [registered, guestToken, houseRules, onGameReady],
  );

  return (
    <div className="create-game" data-testid="create-game-source">
      <h2 className="create-game__title">Create a new game</h2>
      <p className="create-game__subtitle muted">
        Choose who runs the game. An AI builds the board from the categories your players
        suggest.
      </p>

      <HostModeCards value={hostMode} onChange={setHostMode} />

      {/* House Rules (#232, mock 13/18): a preset chooser (Classic vs Game Night) plus an
          Advanced drawer exposing every per-game option (power-ups, buzzer, scoring). It
          reports the resolved, bounded houseRules up; Classic reports `undefined` so the
          create body is unchanged. Applies to both host modes + board sources.

          The host-local "Rate clues" toggle (#201, D201.12) lives inside this drawer's
          Hosting group (#315, DI-315.2). Its value stays owned here and persists to the
          host's browser only — it is NEVER folded into the houseRules DTO / the wire. In
          auto-host mode the chooser disables it (no human host to rate). */}
      <HouseRulesChooser
        onChange={setHouseRules}
        hostMode={hostMode}
        rateClues={rateClues}
        onRateCluesChange={(enabled) => {
          setRateClues(enabled);
          saveRateCluesEnabled(enabled);
        }}
        savedPresets={savedPresets}
        lastUsed={lastUsed}
        canSaveRules={registered && guestToken !== null}
        onSavePreset={handleSavePreset}
        onDeletePreset={handleDeletePreset}
      />

      {/* Show the balance + the single get-more-credits slot (FR-9.5) for a paid game,
          up front (#171 review). Hidden when the game is free (human-hosted pack) or when
          there is no identity to read a balance for (dev). Reports the balance up so the
          create button can be hidden when the host is out of credits (PR #179). */}
      {paidPath && guestToken && (
        <CreditBalance
          guestToken={guestToken}
          registered={registered}
          onBalanceChange={setBalance}
          successDelta={successDelta}
        />
      )}

      {usePack ? (
        <PackLobby
          onGameReady={handleGameReady}
          hostMode={hostMode}
          canCreate={!outOfCredits}
          availableCredits={balance?.total}
          houseRules={houseRules}
        />
      ) : (
        <DynamicLobby
          onGameReady={handleGameReady}
          hostMode={hostMode}
          canCreate={!outOfCredits}
          availableCredits={balance?.total}
          houseRules={houseRules}
        />
      )}
    </div>
  );
}
